import {
  Component,
  OnInit,
  ElementRef,
  Input,
  HostListener,
} from '@angular/core';
import { ActivatedRoute, Router, NavigationStart } from '@angular/router';
import {
  CourseVariant,
  CourseVariantsService,
  Label,
  LabelsService,
} from 'src/eduframe';
import { environment } from 'src/environments/environment';
import { GlobalVariablesService } from '../../../_services/globalVariables/globalVariables.service';

interface FilterCategory {
  name: string;
  items: FilterItem[];
}
interface FilterItem {
  id: number;
  name: string;
  label: Label;
}

@Component({
  selector: 'en-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.less'],
})
export class FilterComponent implements OnInit {
  @Input() activeCourseType: string;
  @Input() color: string;

  // Active filtering for labels
  public activeCat: any;
  public whiteBackground = false;
  public activeTag: any;
  public activeFilter: string | undefined;

  // categories dropdown
  public categoriesActive = false;

  // Eduframe approach
  public eduframeLabels: Label[] = [];
  public eduframeCourseVariants: CourseVariant[] = [];
  public filters: FilterCategory[] = [];
  public courseVariantId: number | null = null;

  // Filter button visibility
  public courseFilterIsActive: boolean;

  // Instead of a separate inProgressActive, we now use activeFilter === 'inProgress'
  // For convenience, you can keep a local boolean if you wish:
  public inProgressActive: boolean = false;

  @HostListener('document:click', ['$event'])
  clickout(event: MouseEvent) {
    if (
      this.eRef.nativeElement.querySelector('.categories') &&
      !this.eRef.nativeElement
        .querySelector('.categories')
        .contains(event.target)
    ) {
      this.categoriesActive = false;
    }
  }

  constructor(
    private eRef: ElementRef,
    private route: ActivatedRoute,
    private router: Router,
    private eduframeLabelService: LabelsService,
    private eduframeCourseVariantsService: CourseVariantsService,
    private globalVariablesService: GlobalVariablesService
  ) {
    this.route.queryParams.subscribe(params => {
      this.courseVariantId = params.course_variant_id;
    });

    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        // Reset filters when starting to navigate away
        this.globalVariablesService.resetAllFilters();
      }
    });
  }

  ngOnInit(): void {
    this.getFilters();
    this.getCourseVariants();

    // Check URL query params and update active filter accordingly.
    this.route.queryParams.subscribe(params => {
      const labelId = params.label_id;
      const inProgress = params.inProgress;

      if (labelId) {
        this.setActiveFilterById(labelId);
      } else {
        // If no label filter is present, check for inProgress query param.
        if (inProgress !== undefined) {
          // We decide that if inProgress=true, the active filter is 'inProgress'.
          if (inProgress === 'true') {
            this.activeFilter = 'Í gangi';
            this.inProgressActive = true;
            this.globalVariablesService.setActiveFilter('Í gangi');
          } else {
            this.activeFilter = undefined;
            this.inProgressActive = false;
            this.globalVariablesService.setActiveFilter(undefined);
          }
        }
      }
    });

    this.globalVariablesService.courseFilterIsActive$.subscribe(
      (state: boolean) => {
        this.courseFilterIsActive = state;
      }
    );

    this.globalVariablesService.activeFilter$.subscribe(
      (filter: string | undefined) => {
        this.activeFilter = filter;
      }
    );
  }

  private getCourseVariants(): void {
    this.eduframeCourseVariantsService
      .getCourseVariants()
      .subscribe((courseVariants: CourseVariant[]) => {
        this.eduframeCourseVariants = courseVariants.filter(
          (courseVariant: CourseVariant) => courseVariant.name === 'Fjarnám'
        );
      });
  }

  private getFilters(): void {
    this.filters = []; // <-- Clear the array before refilling it

    this.eduframeLabelService
      .getLabels({ modelType: 'Catalog::Product', perPage: 500 })
      .subscribe((labels: Label[]) => {
        if (this.activeCourseType === 'Y0004') {
          this.filters.push({
            name: 'Hvað viltu læra?',
            items: labels
              .filter(label => label.name.startsWith('Nám'))
              .map(label => ({
                id: label.id,
                name: label.name,
                label,
              })),
          });
        } else {
          this.filters.push({
            name: 'Hvað viltu læra?',
            items: labels
              .filter(label => !label.name.startsWith('Nám'))
              .map(label => ({
                id: label.id,
                name: label.name,
                label,
              })),
          });
        }

        const labelId = this.route.snapshot.queryParams['label_id'];
        if (labelId) {
          this.setActiveFilterById(labelId);
        }
      });
  }


  private setActiveFilterById(labelId: string): void {
    if (!labelId) {
      this.clearActiveFilter();
      return;
    }
    for (const filterCategory of this.filters) {
      const matchedItem = filterCategory.items.find(
        item => item.label.id === +labelId
      );
      if (matchedItem) {
        this.activeFilter = matchedItem.label.name;
        this.activeTag = matchedItem.label.name;
        this.globalVariablesService.setActiveFilter(matchedItem.label.name);
        break;
      }
    }
  }

  private clearActiveFilter(): void {
    this.activeFilter = undefined;
    this.activeTag = undefined;
    this.globalVariablesService.setActiveFilter(undefined);
  }

  public toggleCat(): void {
    this.categoriesActive = !this.categoriesActive;
  }

  public filterLabel(event: PointerEvent, label: Label): void {
    if (event.ctrlKey) {
      return;
    }
    this.globalVariablesService.setActiveFilter(label.name);
    this.categoriesActive = false;
    this.globalVariablesService.setButtonHiddenState(true);
  }

  public removeFilter(): void {
    this.globalVariablesService.setActiveFilter(undefined);
    this.globalVariablesService.setButtonHiddenState(false);
  }

  // New: Toggle Í gangi filter using the activeFilter mechanism.
  public toggleinProgressFilter(): void {
    if (this.activeFilter === 'Í gangi') {
      // Untoggle: clear the inProgress filter
      this.globalVariablesService.setActiveFilter(undefined);
      this.inProgressActive = false;
      this.router
        .navigate([], {
          queryParams: { inProgress: null },
          queryParamsHandling: 'merge',
        })
        .then(() => {
          this.reloadData(); // Trigger data refresh explicitly.
        });
    } else {
      // Toggle ON: set active filter to 'inProgress'
      this.globalVariablesService.setActiveFilter('Í gangi');
      this.inProgressActive = true;
      this.router
        .navigate([], {
          queryParams: { inProgress: true },
          queryParamsHandling: 'merge',
        })
        .then(() => {
          this.reloadData(); // Trigger data refresh explicitly.
        });
    }
  }

  private reloadData(): void {
    // This method should call the same services/methods that fetch data for label filters.
    // For example, re-invoking your data fetching functions:
    this.getFilters();
    this.getCourseVariants();
    // OR if data fetching is handled in a separate component/subscription,
    // make sure that component properly reacts to the global filter change.
  }
}
